import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import GenericDropdown from "features/general/GenericDropdown";
import { useAppDispatch, useAppSelector } from "model";
import {
  selectAccessibleFarmerDashboardsByAccessibleUserId,
  selectAllAccessibleFarmerDashboards,
} from "model/accessibleFarmerDashboardsSlice";
import { getMetabaseUrl } from "model/metabaseUrlSlice";
import { setSelectedAccessibleFarmer } from "model/selectedAccessibleFarmer";
import { setSelectedAccessibleFarmerDashboard } from "model/selectedAccessibleFarmerDashboard";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

export default function AccessibleFarmsInsightsDrawer() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const selectedAccessibleFarmer = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedAccessibleFarmer
  );
  const selectedAccessibleFarmerDashboard = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedAccessibleFarmerDashboard
  );
  const accessibleFarmerDashboards = useAppSelector((state) =>
    selectAccessibleFarmerDashboardsByAccessibleUserId(
      state,
      selectedAccessibleFarmer?.guid ?? ""
    )
  );

  const allAccessibleFarmerDashboards = useAppSelector((state) =>
    selectAllAccessibleFarmerDashboards(state)
  );
  const disabledFarmerList: { [key: string]: string } = {};

  for (const userDashboards of allAccessibleFarmerDashboards) {
    if (userDashboards.dashboards.length === 0) {
      disabledFarmerList[userDashboards.guid] = "no accessible dashboard";
    }
  }

  const history = useHistory();
  const dispatch = useAppDispatch();
  const [metabaseUrl, setMetabaseUrl] = useState<string>("");
  const user = useAppSelector((state) => state.user.details);

  const farmerDashboardsAsMap: { [key: string]: string } = {};
  accessibleFarmerDashboards?.dashboards.forEach((dashboard) => {
    farmerDashboardsAsMap[dashboard.visualizationId] = dashboard.title;
  });

  const userNamesAsMap: { [key: string]: string } = {};
  allAccessibleFarmerDashboards.forEach((userDashboards) => {
    userNamesAsMap[userDashboards.guid] = userDashboards.name;
  });

  if (
    !selectedAccessibleFarmer.guid &&
    allAccessibleFarmerDashboards.length > 0
  ) {
    for (const userDashboards of allAccessibleFarmerDashboards) {
      if (userDashboards.dashboards.length > 0) {
        // Default to the first dashboard of the first user if none is selected
        const firstDashboard = userDashboards.dashboards[0];
        if (firstDashboard) {
          dispatch(setSelectedAccessibleFarmer(userDashboards.guid));
          break; // Exit after setting the first available dashboard
        }
      }
    }
  }

  // Load Metabase URL when dashboard is selected (mobile only)
  useEffect(() => {
    if (
      isMobile &&
      selectedAccessibleFarmerDashboard &&
      selectedAccessibleFarmerDashboard.guid &&
      selectedAccessibleFarmer &&
      selectedAccessibleFarmer.guid
    ) {
      const workaroundFunc = async () => {
        const params: { [name: string]: string } = {
          userFilter: selectedAccessibleFarmer.guid ?? "",
        };
        try {
          const res = await dispatch(
            getMetabaseUrl({
              visualizationId: selectedAccessibleFarmerDashboard.guid,
              params,
            })
          ).then(unwrapResult);
          setMetabaseUrl(res.url);
        } catch (e) {
          console.log(e);
        }
      };
      void workaroundFunc();
    }
  }, [
    dispatch,
    selectedAccessibleFarmerDashboard,
    selectedAccessibleFarmer,
    selectedAccessibleFarmerDashboard?.guid,
    selectedAccessibleFarmer?.guid,
    user?.mail,
    user?.name,
    isMobile,
  ]);

  if (
    !selectedAccessibleFarmerDashboard.guid &&
    accessibleFarmerDashboards?.dashboards &&
    (accessibleFarmerDashboards.dashboards.length ?? -1) > 0
  ) {
    // Default to the first dashboard if none is selected
    const firstDashboard = accessibleFarmerDashboards.dashboards[0];
    if (firstDashboard) {
      dispatch(
        setSelectedAccessibleFarmerDashboard({
          guid: firstDashboard.visualizationId,
          title: firstDashboard.title,
        })
      );
    }
  } else if (
    selectedAccessibleFarmerDashboard.guid &&
    accessibleFarmerDashboards?.dashboards &&
    (accessibleFarmerDashboards.dashboards.length ?? -1) === 0
  ) {
    dispatch(
      setSelectedAccessibleFarmerDashboard({
        guid: null,
        title: null,
      })
    );
  }

  return (
    <>
      <PageTitleBar title={t("go.to.farm.insights")} showDivider />
      {allAccessibleFarmerDashboards.length === 0 && (
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: 3,
            boxShadow: 1,
            flex: 1, // Take remaining space
            display: "flex",
            alignItems: "top",
            justifyContent: "center",
            minHeight: "calc(100vh - 300px)", // Ensure minimum height even when loading
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t("no.accessible.farmer.dashboards.available")}
          </Typography>
        </Box>
      )}
      {allAccessibleFarmerDashboards.length > 0 && (
        <Container
          maxWidth="sm"
          sx={{
            pt: isMobile ? 2 : 3,
            pb: isMobile ? 10 : 2, // Add bottom padding for mobile bottom navigation
            height: isMobile ? "calc(100vh - 120px)" : "auto", // Increased height - less space reserved
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid item xs={12} sm={6} display="flex" alignItems="baseline">
            <GenericDropdown
              required={false}
              disabled={false}
              flipKeyValue={true}
              onChange={(event) => {
                const selectedUser = allAccessibleFarmerDashboards.filter(
                  (d) => d.guid === event.target.value
                )[0];
                dispatch(setSelectedAccessibleFarmer(selectedUser.guid));
                // Clear the previous Metabase URL when selecting a new dashboard
                if (isMobile) {
                  setMetabaseUrl("");
                }
              }}
              currentValue={selectedAccessibleFarmer.guid}
              label={"farmer.dashboard.farmer.selection.label"}
              name={`selectedFarmerDashboard`}
              possibleValues={userNamesAsMap}
              tPrefix={"reseedEvent.totalAmountUnit.label"}
              inputError={false}
              shouldTranslate={false}
              itemDisabled={disabledFarmerList}
            />
          </Grid>
          <Grid item xs={12} sm={6} display="flex" alignItems="baseline">
            <GenericDropdown
              required={false}
              disabled={
                (accessibleFarmerDashboards?.dashboards?.length ?? 0) < 1
              }
              flipKeyValue={true}
              onChange={(event) => {
                const selectedDashboard =
                  accessibleFarmerDashboards?.dashboards.filter(
                    (d) => d.visualizationId === event.target.value
                  )[0];

                dispatch(
                  setSelectedAccessibleFarmerDashboard({
                    guid: selectedDashboard?.visualizationId,
                    title: selectedDashboard?.title,
                  })
                );

                // Clear the previous Metabase URL when selecting a new dashboard
                if (isMobile) {
                  setMetabaseUrl("");
                }
              }}
              currentValue={selectedAccessibleFarmerDashboard.guid}
              label={"farmer.dashboard.dashboard.selection.label"}
              name={`selectedFarmerDashboard`}
              possibleValues={farmerDashboardsAsMap}
              tPrefix={"reseedEvent.totalAmountUnit.label"}
              inputError={false}
              shouldTranslate={false}
            />
          </Grid>

          {/* Mobile Dashboard iframe */}
          {isMobile && (
            <Grid
              item
              xs={12}
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              {metabaseUrl ? (
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: 1,
                    overflow: "hidden",
                    flex: 1, // Take remaining space
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <iframe
                    frameBorder="0"
                    src={metabaseUrl}
                    width="100%"
                    height="100%"
                    title="Farm-insights"
                    style={{ border: "none", flex: 1 }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 2,
                    p: 3,
                    boxShadow: 1,
                    flex: 1, // Take remaining space
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "calc(100vh - 300px)", // Ensure minimum height even when loading
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Loading dashboard...
                  </Typography>
                </Box>
              )}
            </Grid>
          )}
        </Container>
      )}
    </>
  );
}
